import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { apiError } from '../../services/api';
import { useApi } from '../../hooks/useApi';
import Select from '../../components/ui/Select';
import { Card, Field, FormActions, LoadingRows, Notice, PageHeader } from '../../components/ui/Page';
import type { Partner } from './index';

const EMPTY = { name: '', email: '', phone: '', firm: '', focus: '', capacity: '', status: 'INVITED' as Partner['status'] };

export default function PartnerFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(id);
  const { data, loading } = useApi<{ partners: Partner[] }>(editing ? '/admin/partners' : null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const existing = data?.partners.find((p) => p.id === id);

  useEffect(() => {
    if (existing) {
      setForm({
        name: existing.name,
        email: existing.email,
        phone: existing.phone || '',
        firm: existing.firm || '',
        focus: existing.focus || '',
        capacity: existing.capacity || '',
        status: existing.status,
      });
    }
  }, [existing]);

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const body = { ...form, phone: form.phone || null, firm: form.firm || null, focus: form.focus || null, capacity: form.capacity || null };
    try {
      if (editing) await api.patch(`/admin/partners/${id}`, body);
      else await api.post('/admin/partners', body);
      navigate('/partners', { replace: true });
    } catch (err) {
      setError(apiError(err, 'Could not save the partner.'));
    } finally {
      setSaving(false);
    }
  };

  if (editing && loading) return <LoadingRows rows={5} />;
  if (editing && !loading && !existing) return <Notice tone="error">Partner not found.</Notice>;

  return (
    <div className="max-w-2xl">
      <PageHeader
        crumbs={[{ label: 'Partners', to: '/partners' }, { label: editing ? existing?.name || 'Edit' : 'New partner' }]}
        title={editing ? 'Edit partner' : 'Add partner'}
        description={editing ? 'Update contact details, thesis or status.' : 'New partners receive a welcome email when mail is configured.'}
      />
      <Card>
        <form onSubmit={onSubmit} className="grid gap-4 sm:grid-cols-2">
          <Field label="Full name" htmlFor="p-name">
            <input id="p-name" required minLength={2} className="mz-input" value={form.name} onChange={set('name')} />
          </Field>
          <Field label="Email" htmlFor="p-email">
            <input id="p-email" required type="email" className="mz-input" value={form.email} onChange={set('email')} />
          </Field>
          <Field label="Phone" htmlFor="p-phone" hint="Optional">
            <input id="p-phone" className="mz-input" value={form.phone} onChange={set('phone')} />
          </Field>
          <Field label="Firm" htmlFor="p-firm" hint="Optional">
            <input id="p-firm" className="mz-input" value={form.firm} onChange={set('firm')} placeholder="Northbridge Capital" />
          </Field>
          <Field label="Investment focus" htmlFor="p-focus" className="sm:col-span-2">
            <input id="p-focus" className="mz-input" value={form.focus} onChange={set('focus')} placeholder="Working capital · MSME manufacturing · India" />
          </Field>
          <Field label="Ticket size" htmlFor="p-cap">
            <input id="p-cap" className="mz-input" value={form.capacity} onChange={set('capacity')} placeholder="INR 50L – 5 Cr" />
          </Field>
          <Field label="Status" htmlFor="p-status">
            <Select
              id="p-status"
              value={form.status}
              onChange={(v) => setForm((f) => ({ ...f, status: v }))}
              options={[
                { value: 'INVITED', label: 'Invited', description: 'Added, not yet engaged' },
                { value: 'ACTIVE', label: 'Active', description: 'Receives rated companies' },
                { value: 'PAUSED', label: 'Paused', description: 'Skipped when sharing' },
              ]}
            />
          </Field>
          {error && (
            <Notice tone="error" className="sm:col-span-2">
              {error}
            </Notice>
          )}
          <div className="sm:col-span-2">
            <FormActions>
              <button type="button" className="mz-btn-ghost" onClick={() => navigate('/partners')}>
                Cancel
              </button>
              <button type="submit" disabled={saving} className="mz-btn-primary">
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Add partner'}
              </button>
            </FormActions>
          </div>
        </form>
      </Card>
    </div>
  );
}
